import { useQueryClient } from "@tanstack/react-query";
import { Wand2 } from "lucide-react";
import React, { useState } from "react";

import { previewInvoice } from "@/api/invoices";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NomenclaturePicker } from "@/pages/invoices/NomenclaturePicker";
import type { MissingMappingGroup } from "@/lib/batchAggregation";
import { useConnection } from "@/auth/ConnectionContext";

interface Props {
  groups: MissingMappingGroup[];
}

interface ActiveFix {
  group: MissingMappingGroup;
  kontragentRefKey: string;
  organizationRefKey: string;
}

export function MissingMappingsPanel({
  groups,
}: Props): React.ReactElement | null {
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [loading, setLoading] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveFix | null>(null);

  if (groups.length === 0) return null;

  const handleFix = async (group: MissingMappingGroup) => {
    setLoading(group.source_name);
    try {
      const preview = await previewInvoice(group.sample_invoice_id);
      const kontragentRefKey = preview.kontragent.selected?.ref_key ?? "";
      const organizationRefKey = preview.organization.selected?.ref_key ?? "";
      if (!kontragentRefKey || !organizationRefKey) {
        // Can't fix mapping without resolved 1С refs — user has to open the
        // individual preview page and resolve organization/kontragent first.
        alert(
          "Не удалось определить контрагента или организацию в 1С. " +
            "Откройте файл через таблицу, сопоставьте оставшиеся блоки вручную.",
        );
        return;
      }
      setActive({ group, kontragentRefKey, organizationRefKey });
    } finally {
      setLoading(null);
    }
  };

  const handleClose = () => {
    setActive(null);
    // Invalidate the list so readiness re-computes and the fixed group drops.
    queryClient.invalidateQueries({ queryKey: ["invoices", connId] });
  };

  return (
    <>
      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-amber-800">
            <Wand2 className="size-4" />
            Нужны сопоставления ({groups.length})
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Исправьте одно соответствие — все связанные файлы станут готовы к
            созданию в 1С.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {groups.map((g) => (
            <div
              key={`${g.kontragent_inn}||${g.source_name}`}
              className="flex items-center justify-between gap-3 rounded-md border bg-background p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {g.source_name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {g.kontragent_label}
                  {g.kontragent_inn && <> · ИНН {g.kontragent_inn}</>}
                  {" · "}
                  <span className="font-medium text-foreground">
                    {g.file_count}{" "}
                    {g.file_count === 1
                      ? "файл"
                      : g.file_count < 5
                        ? "файла"
                        : "файлов"}
                  </span>
                  {g.kind === "incomplete" && (
                    <> · требуется обновить договор/счета</>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={loading === g.source_name}
                onClick={() => handleFix(g)}
              >
                {loading === g.source_name ? "…" : "Исправить"}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {active && (
        <NomenclaturePicker
          open={true}
          sourceName={active.group.source_name}
          suggestions={[]}
          invoiceId={active.group.sample_invoice_id}
          kontragentRefKey={active.kontragentRefKey}
          organizationRefKey={active.organizationRefKey}
          onClose={handleClose}
        />
      )}
    </>
  );
}
